/**
 * WAI-ARIA Tabs pattern utility.
 *
 * Applies proper ARIA roles and attributes to a tab container:
 *   - tablist container: role="tablist"
 *   - tab buttons: role="tab", aria-selected, aria-controls, id
 *   - tab panel: role="tabpanel", aria-labelledby, id
 *
 * Adds left/right arrow key navigation per the ARIA Authoring Practices.
 * Returns a cleanup function to remove the keyboard listener.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */

let ariaIdCounter = 0;

function nextId(prefix: string): string {
  return `${prefix}-${++ariaIdCounter}`;
}

export interface AriaTabsOptions {
  /** The container element holding all tab buttons. */
  tabList: HTMLElement;
  /** CSS selector to find tab buttons within the tabList. */
  tabSelector: string;
  /** The panel element that displays the active tab's content. */
  panel: HTMLElement;
  /** Unique prefix for generated IDs (e.g., 'feed', 'sources'). */
  prefix: string;
}

/**
 * Apply ARIA tab roles and keyboard navigation.
 * Call after initial tab rendering AND after re-rendering tabs.
 * Returns a cleanup function that removes the keyboard listener.
 */
export function applyAriaTabs(options: AriaTabsOptions): () => void {
  const { tabList, tabSelector, panel, prefix } = options;

  // Set tablist role
  tabList.setAttribute('role', 'tablist');

  // Panel setup
  const panelId = panel.id || nextId(`${prefix}-tabpanel`);
  panel.id = panelId;
  panel.setAttribute('role', 'tabpanel');

  // Apply roles to each tab button
  const tabs = tabList.querySelectorAll<HTMLElement>(tabSelector);
  let activeTabId = '';

  tabs.forEach((tab) => {
    const tabId = nextId(`${prefix}-tab`);
    tab.id = tabId;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', panelId);

    const isActive = tab.classList.contains('active');
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');

    if (isActive) activeTabId = tabId;
  });

  // Link panel back to active tab
  if (activeTabId) {
    panel.setAttribute('aria-labelledby', activeTabId);
  }

  // Arrow key navigation
  function handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    if (!target.matches(tabSelector)) return;

    const currentTabs = Array.from(
      tabList.querySelectorAll<HTMLElement>(tabSelector),
    );
    const idx = currentTabs.indexOf(target);
    if (idx === -1) return;

    let nextIdx = -1;

    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % currentTabs.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (idx - 1 + currentTabs.length) % currentTabs.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = currentTabs.length - 1;
    }

    if (nextIdx >= 0 && nextIdx !== idx) {
      e.preventDefault();
      const nextTab = currentTabs[nextIdx];
      nextTab.focus();
      nextTab.click(); // Activate the tab
    }
  }

  tabList.addEventListener('keydown', handleKeydown);

  return () => {
    tabList.removeEventListener('keydown', handleKeydown);
  };
}

/**
 * Update ARIA attributes after a tab switch (when active class changes).
 * Call this inside the tab click handler after toggling the active class.
 */
export function updateAriaTabSelection(
  tabList: HTMLElement,
  tabSelector: string,
  panel: HTMLElement,
): void {
  const tabs = tabList.querySelectorAll<HTMLElement>(tabSelector);
  tabs.forEach((tab) => {
    const isActive = tab.classList.contains('active');
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
    if (isActive && tab.id) {
      panel.setAttribute('aria-labelledby', tab.id);
    }
  });
}
